/**
 * Application_Form — ranked title-preferences pure logic
 * (application-form-builder spec, task 5.1).
 *
 * This module holds the I/O-free heart of Requirement 19: an Applicant's
 * free-text title requests are captured as a ranked list (`Title_Preferences`)
 * stored on the `ApplicationResponse`, while `Participation.approvedTitle`
 * remains the single title source-of-truth a Director sets and merit reads.
 *
 * It is exercised both by the `application-form-service` Lambda (which loads the
 * cross-applicant `approvedTitle` list and persists the ranked preferences) and
 * by `jest` + `fast-check` / the frontend `vitest` suite, exactly like the
 * `division-registry/visibility.ts` and `person-service/personLogic.ts`
 * precedents. There is no AWS SDK and no I/O here — only the rules.
 *
 * What lives here:
 *   - `normalizeRanked` — trim, drop blanks, preserve rank order, dedupe
 *     (Req 19.1, 19.2).
 *   - `isValidPreference` — a preference is valid iff it is non-empty after trim.
 *   - `titleContentionWarnings` — the non-blocking `Title_Contention_Warning`
 *     rule: a typed preference that equals an existing approvedTitle of ANOTHER
 *     applicant in the same Competition (case-insensitive) is contended; the
 *     function returns warnings only and never blocks submission (Req 19.3, 19.4).
 */

// ---------------------------------------------------------------------------
// Types

/**
 * A ranked list of free-text title requests authored by an Applicant. Stored on
 * the `ApplicationResponse` (the `titlePreferences` slot), outside the generic
 * custom-field `values` map, so the no-stored-bound-value invariant on `values`
 * still holds verbatim (Req 19.2).
 */
export interface TitlePreferences {
  ranked: string[];
}

// ---------------------------------------------------------------------------
// Validation / normalization

/**
 * Whether a single title preference is valid: non-empty after trimming
 * surrounding whitespace. A blank or whitespace-only entry carries no request
 * and is not a valid preference.
 */
export function isValidPreference(text: string): boolean {
  return typeof text === 'string' && text.trim().length > 0;
}

/**
 * Normalize a ranked preference list (Req 19.1, 19.2):
 *   1. trim each entry,
 *   2. drop blank / whitespace-only entries,
 *   3. preserve the applicant's rank order,
 *   4. dedupe case-insensitively, keeping the first (highest-ranked) occurrence.
 *
 * Pure and total: the result is always a clean, duplicate-free ranked list.
 */
export function normalizeRanked(input: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input) {
    if (!isValidPreference(raw)) {
      continue;
    }
    const trimmed = raw.trim();
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

/**
 * Cap a ranked preference list to at most `max` entries
 * (director-form-application-cutover spec, Req 5.1, 5.3, 5.4).
 *
 * Runs the full `normalizeRanked` pipeline (trim, drop blanks, preserve rank
 * order, dedupe case-insensitively) and then keeps only the first `max`
 * entries — i.e. the applicant's top-`max` ranked title preferences. The
 * Title_Preferences control accepts between zero and three rows, so the
 * default cap is three.
 *
 * Pure and total: the result is always a clean, duplicate-free ranked list of
 * at most `max` entries.
 */
export function capRankedPreferences(input: string[], max = 3): string[] {
  return normalizeRanked(input).slice(0, max);
}

// ---------------------------------------------------------------------------
// Title contention (non-blocking)

/**
 * The non-blocking `Title_Contention_Warning` rule (Req 19.3, 19.4).
 *
 * A typed preference is "contended" when it equals — case-insensitively — an
 * existing `Participation.approvedTitle` held by ANOTHER applicant in the same
 * Competition. The applicant's own approved title (matched by `selfPersonId`) is
 * never a contention against their own preferences.
 *
 * Returns the contended preferences (normalized rank order, deduped) — warnings
 * only. This function NEVER blocks submission; the caller surfaces the returned
 * list as advisory warnings (Req 19.4).
 *
 * @param prefs                  the applicant's ranked title preferences
 * @param existingApprovedTitles other applicants' approved titles in the competition
 * @param selfPersonId           the applicant's own personId (excluded from contention)
 */
export function titleContentionWarnings(
  prefs: TitlePreferences,
  existingApprovedTitles: { personId: string; approvedTitle: string }[],
  selfPersonId: string,
): string[] {
  // The set of approved titles to contend against: every OTHER applicant's
  // non-blank approvedTitle, compared case-insensitively.
  const contendedTitles = new Set<string>();
  for (const entry of existingApprovedTitles) {
    if (entry.personId === selfPersonId) {
      continue;
    }
    if (!isValidPreference(entry.approvedTitle)) {
      continue;
    }
    contendedTitles.add(entry.approvedTitle.trim().toLowerCase());
  }

  // A preference is a warning iff it matches one of those approved titles.
  return normalizeRanked(prefs.ranked).filter((pref) =>
    contendedTitles.has(pref.toLowerCase()),
  );
}
